import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { ChatMessagePrompt } from '~/components/chat-message-prompt/ChatMessagePrompt';
import { useActions } from '~/stores/use-actions/useActions';
import { hasActions } from '~/stores/use-actions/utils/hasActions';
import { MessageReply } from '~/stores/use-chat/use-chat.types';
import { useChat } from '~/stores/use-chat/useChat';

import * as S from './AdditionalResponse.styles';

export type AdditionalResponseProps = {
  replies: MessageReply[];
  question: string;
  messageId: string;
};

export const AdditionalResponse = ({
  replies,
  question,
  messageId,
}: AdditionalResponseProps) => {
  const { t } = useTranslation('intel');
  const setActions = useActions(state => state.setActions);
  const { append, removeReply } = useChat();

  const createNewMessage = (reply: MessageReply) => {
    append({
      status: 'complete',
      id: v4(),
      question,
      reply: [reply],
      hasSeen: false,
    });
    removeReply(messageId, reply.source);

    if (hasActions(reply.data)) {
      setActions(reply.data.actions);
    }
  };

  const getTitle = (source: string) => {
    /* i18next-extract-mark-context-next-line ["asset-overview-assistant", "fallback-assistant", "chart-assistant", "document-assistant", "scenario-assistant", "screening-assistant", "watchlist-assistant"] */
    const title = t('intel:additionalResponse.titles.title', {
      context: source,
    });

    if (source === 'fallback-assistant') {
      return (
        <S.FallbackWrapper data-testid="fallback-title">
          <span>
            <S.FallbackIcon />
          </span>
          <S.FallbackText>{title}</S.FallbackText>
        </S.FallbackWrapper>
      );
    }

    return title;
  };

  return (
    <>
      <S.Text>{t('intel:additionalResponse.description')}</S.Text>
      <S.Container>
        {replies.map(reply => {
          return (
            <ChatMessagePrompt
              data-testid="additional-replies"
              key={reply.source}
              title={getTitle(reply.source)}
              message={''}
              onClick={() => createNewMessage(reply)}
            />
          );
        })}
      </S.Container>
    </>
  );
};
